import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { HeartHandshake, IndianRupee, MapPin, User, Loader2, Search, CheckCircle2, ArrowRight, ArrowLeft, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { UP_DISTRICTS } from '@/lib/districts';

/**
 * SaathiOnboardingWizard
 * -----------------------
 * A 3-step first-login wizard for Solar Saathi field agents:
 *   1) Confirm profile basics (name + mobile)
 *   2) Enter UPI id for commission payouts
 *   3) Pick territory (assigned districts)
 *
 * The wizard is mounted globally by AdminLayout and auto-opens when the
 * logged-in user is a saathi AND `saathi_onboarded !== true`. On successful
 * submission the backend sets `saathi_onboarded=true`, so the wizard never
 * appears again.
 */
export default function SaathiOnboardingWizard() {
    const { user, refreshMe } = useAuth();
    const shouldShow = !!user && user.role === 'saathi' && !user.saathi_onboarded;

    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);

    // Form state
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    const [upi, setUpi] = useState('');
    const [districts, setDistricts] = useState([]);
    const [districtQuery, setDistrictQuery] = useState('');

    // Open the dialog only when we detect a saathi who hasn't onboarded yet.
    // We split this into an effect so the dialog can animate in cleanly.
    useEffect(() => {
        if (shouldShow) {
            setStep(0);
            setName(user?.name || '');
            setMobile(user?.mobile || '');
            setUpi(user?.saathi_upi || '');
            setDistricts(Array.isArray(user?.assigned_districts) ? user.assigned_districts : []);
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [shouldShow, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const totalSteps = 3;
    const progress = ((step + 1) / totalSteps) * 100;

    const upiValid = /^[\w.\-]{2,}@[\w\-]{2,}$/.test((upi || '').trim());
    const filteredDistricts = useMemo(() => {
        const q = districtQuery.trim().toLowerCase();
        if (!q) return UP_DISTRICTS;
        return UP_DISTRICTS.filter(d => d.toLowerCase().includes(q));
    }, [districtQuery]);

    const canProceed = (
        (step === 0 && (name || '').trim().length >= 2 && /^\d{10}$/.test((mobile || '').replace(/\D/g, ''))) ||
        (step === 1 && upiValid) ||
        (step === 2 && districts.length > 0)
    );

    const toggleDistrict = (d) => {
        setDistricts(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    };

    const submit = async () => {
        if (busy) return;
        if (!upiValid) { toast.error('Please enter a valid UPI id'); setStep(1); return; }
        if (!districts.length) { toast.error('Please select at least one district'); setStep(2); return; }
        setBusy(true);
        try {
            await api.saathiCompleteOnboarding({
                name: name.trim() || undefined,
                mobile: (mobile || '').replace(/\D/g, '') || undefined,
                saathi_upi: upi.trim(),
                assigned_districts: districts,
            });
            await refreshMe();
            toast.success('Welcome aboard! Your Saathi profile is ready 🎉');
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Onboarding failed. Please try again.');
        } finally { setBusy(false); }
    };

    if (!shouldShow) return null;

    return (
        <Dialog open={open} onOpenChange={() => { /* prevent close: saathi must finish onboarding */ }}>
            <DialogContent
                className='max-w-lg p-0 overflow-hidden'
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                data-testid='saathi-onboarding-wizard'
            >
                {/* Hero */}
                <div className='px-6 pt-6 pb-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-b'>
                    <DialogHeader className='space-y-1.5'>
                        <div className='flex items-center gap-2'>
                            <div className='w-9 h-9 rounded-full bg-primary/15 text-primary grid place-items-center'>
                                <HeartHandshake className='w-5 h-5' />
                            </div>
                            <Badge variant='outline' className='text-[10px] uppercase tracking-wide'>
                                Solar Saathi
                            </Badge>
                        </div>
                        <DialogTitle className='text-xl font-semibold'>
                            Welcome, {(user?.name || '').split(' ')[0] || 'Saathi'} 👋
                        </DialogTitle>
                        <div className='text-sm text-muted-foreground'>
                            Let's finish your profile so you can start earning commissions on every customer you refer.
                        </div>
                    </DialogHeader>
                    <div className='mt-4 space-y-1.5'>
                        <div className='flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground'>
                            <span>Step {step + 1} of {totalSteps}</span>
                            <span>{Math.round(progress)}%</span>
                        </div>
                        <Progress value={progress} className='h-1.5' />
                    </div>
                </div>

                {/* Body */}
                <div className='px-6 py-5 space-y-4 min-h-[260px]'>
                    {step === 0 && (
                        <StepBlock icon={User} title='Confirm your basics' hint='Customers see this on your Saathi profile.'>
                            <div className='space-y-3'>
                                <div className='space-y-1.5'>
                                    <Label className='text-xs'>Full name</Label>
                                    <Input
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        placeholder='e.g., Ramesh Yadav'
                                        data-testid='saathi-wiz-name'
                                    />
                                </div>
                                <div className='space-y-1.5'>
                                    <Label className='text-xs'>Mobile number</Label>
                                    <Input
                                        value={mobile}
                                        onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        placeholder='10-digit mobile'
                                        inputMode='numeric'
                                        data-testid='saathi-wiz-mobile'
                                    />
                                    <div className='text-[11px] text-muted-foreground'>
                                        This is where you'll receive WhatsApp/SMS updates from admin.
                                    </div>
                                </div>
                            </div>
                        </StepBlock>
                    )}

                    {step === 1 && (
                        <StepBlock icon={IndianRupee} title='Where should we send your commission?' hint="Enter any UPI id — you'll receive payouts here.">
                            <div className='space-y-1.5'>
                                <Label className='text-xs'>UPI id</Label>
                                <Input
                                    value={upi}
                                    onChange={e => setUpi(e.target.value.trim())}
                                    placeholder='yourname@upi'
                                    data-testid='saathi-wiz-upi'
                                    autoCapitalize='off'
                                    autoCorrect='off'
                                    spellCheck='false'
                                />
                                {upi && !upiValid && (
                                    <div className='text-[11px] text-destructive'>Enter a valid UPI id (e.g., 9876543210@ybl)</div>
                                )}
                                <div className='text-[11px] text-muted-foreground'>
                                    Examples: <code className='px-1 py-0.5 bg-muted rounded'>ramesh@ybl</code>,{' '}
                                    <code className='px-1 py-0.5 bg-muted rounded'>9876543210@paytm</code>,{' '}
                                    <code className='px-1 py-0.5 bg-muted rounded'>name@okhdfcbank</code>
                                </div>
                            </div>
                        </StepBlock>
                    )}

                    {step === 2 && (
                        <StepBlock icon={MapPin} title='Which districts do you cover?' hint='Pick one or more — leads from these areas will be routed to you.'>
                            <div className='space-y-2'>
                                <div className='relative'>
                                    <Search className='w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none' />
                                    <Input
                                        value={districtQuery}
                                        onChange={e => setDistrictQuery(e.target.value)}
                                        placeholder='Search districts…'
                                        className='pl-8 h-9'
                                        data-testid='saathi-wiz-district-search'
                                    />
                                </div>
                                {districts.length > 0 && (
                                    <div className='flex flex-wrap gap-1.5 pt-1'>
                                        {districts.map(d => (
                                            <Badge
                                                key={d}
                                                variant='secondary'
                                                className='pl-2 pr-1 py-0.5 gap-1 cursor-pointer hover:bg-destructive/10 hover:text-destructive'
                                                onClick={() => toggleDistrict(d)}
                                                title='Remove'
                                            >
                                                {d} <span className='text-xs opacity-70'>×</span>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                                <ScrollArea className='h-40 rounded-md border bg-muted/20'>
                                    <div className='p-2 grid grid-cols-2 gap-1'>
                                        {filteredDistricts.map(d => {
                                            const checked = districts.includes(d);
                                            return (
                                                <label
                                                    key={d}
                                                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${checked ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                                                    data-testid={`saathi-wiz-district-${d}`}
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        onCheckedChange={() => toggleDistrict(d)}
                                                    />
                                                    <span className='truncate'>{d}</span>
                                                </label>
                                            );
                                        })}
                                        {filteredDistricts.length === 0 && (
                                            <div className='col-span-2 text-center py-6 text-xs text-muted-foreground'>
                                                No districts match "{districtQuery}"
                                            </div>
                                        )}
                                    </div>
                                </ScrollArea>
                                <div className='text-[11px] text-muted-foreground'>
                                    Selected: <span className='font-medium text-foreground'>{districts.length}</span> district{districts.length === 1 ? '' : 's'}
                                </div>
                            </div>
                        </StepBlock>
                    )}
                </div>

                {/* Footer / actions */}
                <div className='px-6 py-4 border-t bg-muted/30 flex items-center justify-between gap-2'>
                    <Button
                        variant='ghost'
                        size='sm'
                        disabled={step === 0 || busy}
                        onClick={() => setStep(s => Math.max(0, s - 1))}
                        data-testid='saathi-wiz-back'
                    >
                        <ArrowLeft className='w-4 h-4 mr-1' /> Back
                    </Button>
                    <div className='flex items-center gap-1.5'>
                        {Array.from({ length: totalSteps }).map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 w-6 rounded-full transition-colors ${i <= step ? 'bg-primary' : 'bg-muted'}`}
                            />
                        ))}
                    </div>
                    {step < totalSteps - 1 ? (
                        <Button
                            size='sm'
                            disabled={!canProceed || busy}
                            onClick={() => setStep(s => Math.min(totalSteps - 1, s + 1))}
                            data-testid='saathi-wiz-next'
                        >
                            Next <ArrowRight className='w-4 h-4 ml-1' />
                        </Button>
                    ) : (
                        <Button
                            size='sm'
                            disabled={!canProceed || busy}
                            onClick={submit}
                            data-testid='saathi-wiz-finish'
                        >
                            {busy ? (
                                <><Loader2 className='w-4 h-4 mr-1 animate-spin' /> Saving…</>
                            ) : (
                                <><PartyPopper className='w-4 h-4 mr-1' /> Finish</>
                            )}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function StepBlock({ icon: Icon, title, hint, children }) {
    return (
        <div className='space-y-3'>
            <div className='flex items-start gap-3'>
                <div className='w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center flex-shrink-0 mt-0.5'>
                    <Icon className='w-4 h-4' />
                </div>
                <div>
                    <div className='font-medium text-sm flex items-center gap-1.5'>
                        {title}
                        <CheckCircle2 className='w-3.5 h-3.5 text-emerald-500 opacity-0 data-[done=true]:opacity-100' />
                    </div>
                    {hint && <div className='text-xs text-muted-foreground'>{hint}</div>}
                </div>
            </div>
            <div className='pl-11'>{children}</div>
        </div>
    );
}
